import { useState, useCallback } from 'react';
import type { TelegramMessage } from '../types';

export function useMessageSelection(messages: TelegramMessage[]) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [lastSelectedId, setLastSelectedId] = useState<number | null>(null);

  const toggleSelection = useCallback((messageId: number, shiftKey = false) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);

      if (shiftKey && lastSelectedId !== null) {
        // Range selection
        const messageIds = messages.map(m => m.id);
        const lastIndex = messageIds.indexOf(lastSelectedId);
        const currentIndex = messageIds.indexOf(messageId);

        if (lastIndex !== -1 && currentIndex !== -1) {
          const start = Math.min(lastIndex, currentIndex);
          const end = Math.max(lastIndex, currentIndex);

          for (let i = start; i <= end; i++) {
            newSet.add(messageIds[i]);
          }
        }
      } else {
        // Single toggle
        if (newSet.has(messageId)) {
          newSet.delete(messageId);
        } else {
          newSet.add(messageId);
        }
      }

      return newSet;
    });

    setLastSelectedId(messageId);
  }, [messages, lastSelectedId]);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(messages.map(m => m.id)));
  }, [messages]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setLastSelectedId(null);
  }, []);

  const isSelected = useCallback((messageId: number) => {
    return selectedIds.has(messageId);
  }, [selectedIds]);

  const selectedMessages = messages.filter(m => selectedIds.has(m.id));

  return {
    selectedIds,
    selectedMessages,
    selectedCount: selectedIds.size,
    toggleSelection,
    selectAll,
    clearSelection,
    isSelected,
  };
}
